import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Section from '../components/common/Section';
import CallToAction from '../components/sections/CallToAction';
import Button from '../components/common/Button';
import { 
  FaChartLine, 
  FaExchangeAlt, 
  FaShieldAlt, 
  FaDatabase, 
  FaClipboardCheck, 
  FaNetworkWired,
  FaArrowRight
} from 'react-icons/fa';

const ServicesContainer = styled.div``;

const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  background-color: var(--primary-color);
  color: white;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ServiceItem = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:nth-child(even) {
    direction: rtl;
    
    .content {
      direction: ltr;
    }
  }
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    
    &:nth-child(even) {
      direction: ltr;
    }
  }
`;

const ServiceImage = styled.div`
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  background-color: var(--primary-light);
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const ServiceContent = styled.div`
  padding: 1rem 0;
  
  @media (max-width: 992px) {
    text-align: center;
  }
`;

const ServiceTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ServiceDescription = styled.p`
  color: var(--text-light);
  margin-bottom: 2rem;
  line-height: 1.8;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
`;

const ServiceFeature = styled.li`
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  
  svg {
    color: var(--accent-color);
    margin-right: 1rem;
    margin-top: 0.25rem;
  }
  
  @media (max-width: 992px) {
    justify-content: center;
    text-align: left;
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8 }
  }
};

const Services = () => {
  const services = [
    {
      title: 'US Energy Trading Risk Management',
      description: 'Comprehensive US gas and energy trading risk management solution providing traders and risk managers with tools to identify, measure, and manage price risk in volatile US energy markets with CFTC compliance monitoring.',
      image: '/services/market-risk.jpg',
      link: '/services/market-risk-management',
      features: [
        'Real-time US energy P&L and position monitoring',
        'CFTC compliant stress testing and scenario analysis',
        'US energy market VaR and risk metrics calculation',
        'Customizable US energy trading dashboards and reporting'
      ]
    },
    {
      title: 'US Energy Trading Data Integration',
      description: 'Seamlessly connect multiple US energy trading systems, data sources, and regulatory platforms to create a unified view of your gas and energy trading data, eliminating data silos and ensuring CFTC compliance.',
      image: '/services/data-integration.jpg',
      link: '/services/trade-data-integration',
      features: [
        'ETL processes for US energy trading data consolidation',
        'Real-time US energy data synchronization across systems',
        'Automated US energy data validation and reconciliation',
        'Flexible APIs for US energy trading system integration'
      ]
    },
    {
      title: 'US Energy Trading Compliance Solutions',
      description: 'Stay ahead of CFTC regulatory requirements with our comprehensive US energy trading compliance management solutions designed specifically for gas and energy trading operations.',
      image: '/services/compliance.jpg',
      link: '/services/compliance-solutions',
      features: [
        'US energy trading surveillance and monitoring',
        'CFTC regulatory reporting automation',
        'US energy trading audit trail and documentation',
        'Energy trading compliance policy management'
      ]
    },
    {
      title: 'Position Limits Monitoring & Management',
      description: 'Monitor and manage position limits across US energy exchanges and CFTC regulatory regimes, avoiding violations and ensuring compliance with changing US energy trading regulations.',
      image: '/services/position-limits.jpg',
      link: '/services/position-limits',
      features: [
        'Real-time US energy position limit monitoring',
        'CFTC pre-trade position limit checks and alerts',
        'Multi-jurisdictional US energy limit tracking',
        'Historical US energy position limit analysis and reporting'
      ]
    },
    {
      title: 'US Energy Trading Reconciliation',
      description: 'Automate the reconciliation of US energy trades, positions, P&L, and fees across multiple systems, reducing errors and operational risk in your gas and energy trading operations.',
      image: '/services/reconciliation.jpg',
      link: '/services/reconciliation',
      features: [
        'US energy trade and position reconciliation',
        'Gas and energy P&L and valuation reconciliation',
        'US energy trading fee and cost reconciliation',
        'Energy trading exception management workflow'
      ]
    },
    {
      title: 'US Energy Exchange Connectivity',
      description: 'Connect directly to major US energy exchanges including NYMEX, ICE, and regional power markets, ensuring reliable and fast execution of gas and energy trades with regulatory compliance.',
      image: '/services/exchange-connectivity.jpg',
      link: '/services/exchange-connectivity',
      features: [
        'Direct market access to US energy exchanges',
        'US energy market data feeds and integration',
        'CFTC compliant order routing and execution',
        'US energy trade capture and confirmation'
      ]
    }
  ];

  return (
    <ServicesContainer>
      <HeroSection>
        <div className="container">
          <HeroTitle>US Energy Trading Compliance Solutions</HeroTitle>
          <HeroSubtitle>
            Comprehensive US gas and energy trading compliance solutions with specialized focus on position limits monitoring, CFTC compliance, and regulatory reporting
          </HeroSubtitle>
        </div>
      </HeroSection>

      <Section
        as={motion.div}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {services.map((service, index) => (
          <ServiceItem 
            key={index} 
            variants={itemVariants}
          >
            <ServiceImage>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h3 style={{ color: 'white' }}>{service.title}</h3>
              </div>
            </ServiceImage>
            <ServiceContent className="content">
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServiceFeatures>
                {service.features.map((feature, i) => (
                  <ServiceFeature key={i}>
                    <FaArrowRight />
                    <span>{feature}</span>
                  </ServiceFeature>
                ))}
              </ServiceFeatures>
              <Button to={`/services/${service.link.replace('/services/', '')}`}>Learn more</Button>
            </ServiceContent>
          </ServiceItem>
        ))}
      </Section>

      <CallToAction />
    </ServicesContainer>
  );
};

export default Services; 